import type { AuthMeResponse } from "@shared/types/auth";
import { canAccess } from "@shared/access/check";
import { browserAccess } from "@shared/access/snapshot";

/**
 * Which audience a session belongs to. Pages are reachable by URL, so the two
 * audiences are separated at the route, both ways.
 */

/** True when this session speaks for a content owner: its home is a content-owner company. */
export function isContentOwnerSession(
  user: AuthMeResponse | null | undefined
): boolean {
  return user?.access?.kind === "content-owner";
}

/** A customer's own staff: the home organisation is a customer or partner company. */
export function isCustomerSession(
  user: AuthMeResponse | null | undefined
): boolean {
  return user?.access?.kind === "customer";
}

/** A user of the Nenda company holding the Nenda admin role on it. */
export function isNendaAdminSession(
  user: AuthMeResponse | null | undefined
): boolean {
  const access = user?.access;
  if (access?.kind !== "nenda") return false;
  return access.grants.some(
    (g) =>
      g.role === "nenda-admin" &&
      g.place.kind === "company" &&
      g.place.id === access.nendaCompanyId
  );
}

/**
 * Where a customer starts: their Overview (Sigge, 2026-09-28/29). The
 * fleet-health dashboard is Nenda's estate view. `/overview` resolves to the
 * Frontlayer or Signage overview by the header's product selector - see
 * `nav/customerOverview.ts`.
 */
export const CUSTOMER_HOME = "/overview";

/** Where a freshly logged-in session belongs. */
export function landingPathFor(
  user: AuthMeResponse | null | undefined
): string {
  if (isContentOwnerSession(user)) return "/supplier/titles";
  if (isCustomerSession(user)) return CUSTOMER_HOME;
  const access = user?.access ? browserAccess(user.access) : null;
  const canViewFleet =
    !!access &&
    canAccess(
      access.user,
      { resource: "screens", level: "view" },
      access.nenda,
      access.tree
    );
  return canViewFleet ? "/analytics" : "/premises";
}
